// HealthIndicator.jsx — PR ⓪+① (DB foundation) optional FE use.
//
// Small TopBar status dot driven by GET /health (app-level ops probe — NOT
// /planner/health, which stays the engine-contract surface per the delta doc).
// Distinguishes three states the stall investigation showed we need:
//   down     — fetch failed / non-2xx        → backend (or edge path) unreachable
//   degraded — db.configured && !db.ok       → app serves, DB unreachable
//   ok       — reachable; db absent/unconfigured counts as ok (delta doc:
//              db.configured=false is EXPECTED until deploy config lands)
//
// Poll cadence ≤ 1/min per the doc; paused while the tab is hidden and
// refreshed on return. Click = manual re-check. The response shape is read
// DEFENSIVELY (ok / db.configured / db.ok / db.error / *version* keys) since
// the delta describes fields, not a frozen schema, and additions are additive
// by standing rule.

import React from 'react';
import { useTheme } from './theme';

const POLL_MS = 60000;

function classify(body) {
  if (!body || typeof body !== 'object') return { state: 'ok', note: 'Engine reachable' };
  const db = body.db;
  if (db && db.configured === true && db.ok === false) {
    return { state: 'degraded', note: `Degraded — DB unreachable${db.error ? `: ${db.error}` : ''}` };
  }
  if (db && db.configured === false) return { state: 'ok', note: 'Engine reachable · DB not configured (expected)' };
  return { state: 'ok', note: 'Engine reachable' };
}

function versionLines(body) {
  const out = [];
  const scan = (obj, prefix) => {
    if (!obj || typeof obj !== 'object') return;
    for (const [k, v] of Object.entries(obj)) {
      if (/version/i.test(k) && (typeof v === 'string' || typeof v === 'number')) {
        out.push(`${prefix}${k}: ${v}`);
      } else if (v && typeof v === 'object' && !Array.isArray(v) && !prefix) {
        scan(v, `${k}.`);
      }
    }
  };
  scan(body, '');
  return out;
}

export default function HealthIndicator({ apiBase }) {
  const { P, MO } = useTheme();
  const [st, setSt] = React.useState({ state: 'checking', note: 'Checking backend…', versions: [] });

  const check = React.useCallback(async () => {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 5000);
    try {
      const resp = await fetch(`${apiBase}/health`, { cache: 'no-store', signal: ctrl.signal });
      if (!resp.ok) {
        // Older backend without /health still counts as reachable.
        setSt({ state: 'ok', note: `Engine reachable (no /health — HTTP ${resp.status})`, versions: [] });
        return;
      }
      let body = null;
      try { body = await resp.json(); } catch { /* non-JSON */ }
      const { state, note } = classify(body);
      setSt({ state, note, versions: versionLines(body) });
    } catch {
      setSt({ state: 'down', note: 'Backend unreachable (network / edge / tunnel)', versions: [] });
    } finally {
      clearTimeout(t);
    }
  }, [apiBase]);

  React.useEffect(() => {
    let timer = null;
    const tick = () => { check(); timer = setTimeout(tick, POLL_MS); };
    const onVis = () => {
      if (document.visibilityState === 'hidden') { if (timer) clearTimeout(timer); timer = null; }
      else if (!timer) tick();
    };
    tick();
    document.addEventListener('visibilitychange', onVis);
    return () => { if (timer) clearTimeout(timer); document.removeEventListener('visibilitychange', onVis); };
  }, [check]);

  const color = st.state === 'ok' ? P.ok : st.state === 'degraded' ? P.warn : st.state === 'down' ? P.danger : P.dim;
  const title = [st.note, ...st.versions, 'Click to re-check'].join('\n');

  return (
    <button onClick={check} title={title} style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      fontFamily: MO, fontSize: 10.5, padding: '5px 6px', borderRadius: 6, cursor: 'pointer',
      background: 'transparent', color: st.state === 'ok' ? P.dim : P.muted, border: 'none',
    }}>
      <span style={{
        width: 7, height: 7, borderRadius: '50%', background: color, flexShrink: 0,
        animation: st.state === 'checking' ? 'mono-pulse 1.2s ease-in-out infinite' : 'none',
      }} />
      API
    </button>
  );
}
